// Sopwith 3D - tick interpolation.
//
// The simulation advances ten times a second; the display runs at whatever
// the monitor does. Each object keeps the pose from the previous tick and the
// current one, and the renderer blends between them by how far through the
// current tick the frame is. Sub-pixel position (the 16-bit fraction) is
// used so motion is smooth even at low speeds.

import { ANGLES } from '../sim/constants';
import type { Obj } from '../sim/types';

export interface Pose {
  x: number;
  y: number;
  /** Heading in turns (0..1), so 0.25 is straight up. */
  heading: number;
  orient: number;
}

/** Any jump larger than this (respawns, wraps) snaps instead of sliding. */
const SNAP_DISTANCE = 48;

export function poseOf(ob: Obj, out: Pose): Pose {
  out.x = ob.x + ob.lx / 65536;
  out.y = ob.y + ob.ly / 65536;
  out.heading = ob.angle / ANGLES;
  out.orient = ob.orient;
  return out;
}

/** Shortest-path blend of two headings in turns. */
export function lerpHeading(a: number, b: number, t: number): number {
  let d = b - a;
  d -= Math.round(d);
  return a + d * t;
}

export function lerpPose(prev: Pose, cur: Pose, t: number, out: Pose): Pose {
  const dx = cur.x - prev.x;
  const dy = cur.y - prev.y;
  if (dx * dx + dy * dy > SNAP_DISTANCE * SNAP_DISTANCE || prev.orient !== cur.orient) {
    out.x = cur.x;
    out.y = cur.y;
    out.heading = cur.heading;
    out.orient = cur.orient;
    return out;
  }
  out.x = prev.x + dx * t;
  out.y = prev.y + dy * t;
  out.heading = lerpHeading(prev.heading, cur.heading, t);
  out.orient = cur.orient;
  return out;
}
